import { useEffect, useState, type ReactNode } from 'react';
import type { DaylightAnchor } from '../daylight/daylight';
import { cn } from '../lib/cn';

export type SkyPhase = 'dawn' | 'day' | 'dusk' | 'night';
export type SkyWeather = 'clear' | 'cloudy' | 'rain' | 'snow' | 'fog' | 'storm';

export interface AmbientSceneProps {
  phase: SkyPhase;
  weather?: SkyWeather;
  /** Night rest: almost black, no sky – only the content (a dim clock). */
  sleep?: boolean;
  children: ReactNode;
  className?: string;
}

/** The sky follows the same sun as the daylight colours. */
export function skyPhase(anchor: DaylightAnchor): SkyPhase {
  if (anchor === 'dawn' || anchor === 'sunrise') return 'dawn';
  if (anchor === 'morning' || anchor === 'noon' || anchor === 'evening') return 'day';
  if (anchor === 'sunset' || anchor === 'dusk') return 'dusk';
  return 'night';
}

// Static class names – Tailwind only sees complete strings.
const SKY: Record<SkyPhase, string> = {
  dawn: 'from-ambient-sky-dawn-top to-ambient-sky-dawn-bottom',
  day: 'from-ambient-sky-day-top to-ambient-sky-day-bottom',
  dusk: 'from-ambient-sky-dusk-top to-ambient-sky-dusk-bottom',
  night: 'from-ambient-sky-night-top to-ambient-sky-night-bottom',
};

/** Fixed, hand-placed – random positions would jump on every render. */
const STARS = [
  [8, 12],
  [17, 30],
  [26, 8],
  [38, 22],
  [47, 6],
  [58, 18],
  [66, 34],
  [74, 10],
  [83, 26],
  [91, 14],
  [12, 44],
  [88, 42],
] as const;
const CLOUDS = [
  { top: '6%', delay: '-30s', opacity: 'opacity-20' },
  { top: '28%', delay: '-120s', opacity: 'opacity-10' },
  { top: '52%', delay: '-200s', opacity: 'opacity-15' },
] as const;

/** Pauses all animation while the page is in the background (battery, CPU). */
function usePageVisible() {
  const [visible, setVisible] = useState(() => document.visibilityState !== 'hidden');
  useEffect(() => {
    const on = () => setVisible(document.visibilityState !== 'hidden');
    document.addEventListener('visibilitychange', on);
    return () => document.removeEventListener('visibilitychange', on);
  }, []);
  return visible;
}

/**
 * A slowly moving sky after time of day and weather – the backdrop of the resting wall display.
 * Deep colours only (no white areas on an always-on screen), light text, decorative layers
 * hidden from screen readers. Animations are pure CSS and stop with reduced motion.
 */
export function AmbientScene({
  phase,
  weather = 'clear',
  sleep = false,
  children,
  className,
}: AmbientSceneProps) {
  const visible = usePageVisible();
  const overcast = ['cloudy', 'rain', 'snow', 'fog', 'storm'].includes(weather);
  const dark = phase === 'night' || phase === 'dusk';
  return (
    <div
      data-paused={visible ? undefined : ''}
      data-sky={sleep ? 'sleep' : phase}
      data-weather={weather}
      className={cn(
        'relative isolate overflow-hidden',
        sleep ? 'bg-ambient-sky-sleep-bg text-ambient-sky-sleep-text' : 'text-ambient-sky-text',
        className,
      )}
    >
      {!sleep && (
        <div aria-hidden className="pointer-events-none absolute inset-0 -z-10">
          <div className={cn('shh-sky-layer shh-sky-breathe bg-linear-to-b', SKY[phase])} />
          <div
            className={cn(
              'shh-sky-layer bg-linear-to-b from-ambient-sky-overcast-top to-ambient-sky-overcast-bottom',
              // by night the overcast grey would brighten the room – only a hint of it
              overcast ? (dark ? 'opacity-30' : 'opacity-80') : 'opacity-0',
            )}
          />
          {dark && !overcast && (
            <div className="absolute inset-0">
              {STARS.map(([left, top], i) => (
                <span
                  key={i}
                  className="shh-star"
                  style={{ left: `${left}%`, top: `${top}%`, animationDelay: `${-i * 1.3}s` }}
                />
              ))}
            </div>
          )}
          {(weather !== 'clear' || phase === 'day') &&
            CLOUDS.map((c) => (
              <span
                key={c.top}
                className={cn('shh-cloud', c.opacity)}
                style={{ top: c.top, animationDelay: c.delay }}
              />
            ))}
          {(weather === 'rain' || weather === 'storm') && <div className="shh-rain" />}
          {weather === 'snow' && <div className="shh-snow" />}
        </div>
      )}
      {children}
    </div>
  );
}
